import * as React from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

interface TitleProps {
  children: React.ReactNode;
  className?: string;
}

interface DescriptionProps {
  children: React.ReactNode;
}

interface ActionsProps {
  children: React.ReactNode;
}

function PageHeaderRoot({ children, className }: PageHeaderProps) {
  return (
    <div className={cn('border-b border-cs-border bg-cs-surface-sidebar px-6 py-4', className)}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-0.5">{children}</div>
      </div>
    </div>
  );
}

function Breadcrumbs({ items }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex items-center gap-1 text-xs text-cs-text-muted">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3 h-3 flex-shrink-0" aria-hidden="true" />}
            {item.href ? (
              <Link href={item.href} className="hover:text-cs-text-secondary transition-colors truncate max-w-[180px]">
                {item.label}
              </Link>
            ) : (
              <span className="text-cs-text-secondary font-medium truncate max-w-[180px]" aria-current="page">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Title({ children, className }: TitleProps) {
  return (
    <h1 className={cn('text-xl font-semibold text-cs-text-primary leading-tight', className)}>
      {children}
    </h1>
  );
}

function Description({ children }: DescriptionProps) {
  return <p className="text-sm text-cs-text-muted">{children}</p>;
}

function Actions({ children }: ActionsProps) {
  return <div className="flex items-center gap-2 flex-shrink-0">{children}</div>;
}

/* Compound component with layout */
export function PageHeader({
  breadcrumbs,
  title,
  description,
  actions,
  className,
}: {
  breadcrumbs?: BreadcrumbItem[];
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border-b border-cs-border bg-cs-surface-sidebar px-6 py-4', className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-0.5">
          {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
          <div className="flex items-baseline gap-3">
            <h1 className="text-xl font-semibold text-cs-text-primary">{title}</h1>
          </div>
          {description && <p className="text-sm text-cs-text-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 flex-shrink-0 pt-1">{actions}</div>}
      </div>
    </div>
  );
}

PageHeaderRoot.Breadcrumbs = Breadcrumbs;
PageHeaderRoot.Title = Title;
PageHeaderRoot.Description = Description;
PageHeaderRoot.Actions = Actions;
